import type { ITenantContext } from "./i-tenant-context";

export interface IResolveTenantContextConfig {

  /** Apex host, e.g. "customer.portal.davaco.cloud". */
  baseHost: string;

  /** Optional querystring (window.location.search) for local-dev override. */
  search?: string;
}

const ENTRA: ITenantContext = { kind: "entra", organization: null };

/**
 * The `?org=` context, for any host that carries no slug of its own.
 *
 * `ng serve` runs on localhost, which has no subdomain, so ?org=starbucks
 * stands in for one, and redirectUri() carries it across the Auth0 round trip.
 *
 * Never consulted for a host that DOES carry a slug -- a real customer
 * subdomain must not be re-pointed by a query parameter. Where there is no slug
 * there is nothing to override, which is why the apex may honour it.
 *
 * Safe because the slug only selects which Auth0 organization the login page
 * targets and grants nothing by itself. Credentials
 * for that organization are still required, and a forged value simply presents
 * a login nobody can complete.
 */
function devOverride(search: string | undefined): ITenantContext | null {
  if (search === undefined || search.length === 0) {
    return null;
  }

  const organization: string | null = new URLSearchParams(search).get("org");
  if (organization !== null && organization.length > 0) {
    return { kind: "auth0", organization };
  }

  return null;
}

/**
 * Maps a hostname to the identity provider that serves it.
 *
 * The slug is the LAST label before baseHost, so a deeper host such as
 * `preview.starbucks.<baseHost>` still resolves to "starbucks".
 *
 * @example Which door each host opens
 *   customer.portal.davaco.cloud           → entra  (Davaco employee door)
 *   starbucks.customer.portal.davaco.cloud → auth0, organization "starbucks"
 *   localhost?org=starbucks                → auth0, organization "starbucks"
 *   anything else                          → entra
 */
export function resolveTenantContext(
  hostname: string,
  config: IResolveTenantContextConfig
): ITenantContext {
  const host: string = hostname.trim()
.toLowerCase();
  const base: string = config.baseHost.trim()
.toLowerCase();

  if (host === base || host === `www.${base}`) {
    // The apex is the staff door. It carries no slug, so there is nothing for
    // ?org= to override, and the local-dev override is honoured here too.
    return devOverride(config.search) ?? ENTRA;
  }

  const suffix = `.${base}`;
  if (host.endsWith(suffix)) {
    const prefix: string = host.slice(0, host.length - suffix.length);
    const slug: string =
      prefix.split(".")
.filter(Boolean)
.pop() ?? "";

    if (slug.length > 0 && slug !== "www") {
      return { kind: "auth0", organization: slug };
    }

    return ENTRA;
  }

  return devOverride(config.search) ?? ENTRA;
}
